<script lang="ts" setup>
import { ref, computed, useTemplateRef, onMounted } from 'vue'
import { useForm, useField } from 'vee-validate'
import { OSwitch } from '@oruga-ui/oruga-next'

import FormMessages from '@tech77/hydra/components/FormMessages.vue'
import FormLegalNotice from '@tech77/hydra/components/FormLegalNotice.vue'
import FieldText from '@tech77/hydra/components/FieldText.vue'
import FieldPhone from '@tech77/hydra/components/FieldPhone.vue'
import FieldHidden from '@tech77/hydra/components/FieldHidden.vue'
import FieldEmail from '@tech77/hydra/components/FieldEmail.vue'

import { createSchema } from './schema'

enum Status {
  pristine = 'PRISTINE',
  dirty = 'DIRTY',
  error = 'ERROR',
  success = 'SUCCESS'
}

const props = withDefaults(
  defineProps<{
    modal?: boolean
    formTitle?: string
    successMessage?: string
    errorMessage?: string
    page?: string
    title?: string
  }>(),
  {
    modal: true,
    formTitle: 'Напишите нам',
    successMessage: 'Спасибо, Ваш запрос отправлен!',
    errorMessage: 'Ошибка при обработке запроса.',
    page: 'No page',
    title: 'No title'
  }
)

const emit = defineEmits<{
  (e: 'close'): void
}>()

const { value } = useField<boolean>(() => 'agreement', undefined, {})
const status = ref<Status>(Status.pristine)
const lock = ref(false)
const form = useTemplateRef<HTMLFormElement | null>('form')

const isNotSuccess = computed(() => status.value !== Status.success)
const isSuccess = computed(() => status.value === Status.success)

const initialValues = {
  WEB_FORM_ID: 10,
  form_text_85: '',
  form_text_86: '',
  form_text_87: '',
  form_date_77: '',
  form_text_78: '',
  form_email_79: '',
  form_text_88: '',
  form_text_89: '',
  form_text_90: '',
  form_text_91: '',
  form_text_92: '',
  form_text_93: '',
  form_text_81: '',
  form_text_82: '',
  form_date_83: '',
  form_date_84: '',
  form_text_94: '',
  form_text_95: '',
  form_text_96: '',
  form_hidden_80: '',
  agreement: false
}

const validationSchema = createSchema()

const {
  handleSubmit,
  resetForm,
  meta,
  setFieldValue
} = useForm({
  initialValues,
  validationSchema,
  initialTouched: {
    WEB_FORM_ID: true,
    form_text_85: false,
    form_text_86: false,
    form_text_87: false,
    form_date_77: false,
    form_text_78: false,
    form_email_79: false,
    form_text_88: false,
    form_text_89: false,
    form_text_90: false,
    form_text_91: false,
    form_text_92: false,
    form_text_93: false,
    form_text_81: false,
    form_text_82: false,
    form_date_83: false,
    form_date_84: false,
    form_text_94: false,
    form_text_95: false,
    form_text_96: false,
    form_hidden_80: false,
    agreement: false
  }
})

const birthDate = ref('')
const hasError = ref(false)
const isTouched = ref(false)

function validateDate() {
  isTouched.value = true
  hasError.value = !birthDate.value.trim()
  setFieldValue('form_date_77', birthDate.value)
}

function formatDate(date: string | Date) {
  const d = new Date(date)
  const day = String(d.getDate()).padStart(2, '0')
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const year = d.getFullYear()
  return `${day}.${month}.${year}`
}

function openCalendar(event: MouseEvent) {
  const target = event.target as HTMLElement
  const input = target.previousElementSibling as HTMLInputElement | null

  if (!window.BX?.calendar) {
    console.warn('Требуется window.BX.calendar')
    return
  }

  if (!input) {
    console.warn('Поле даты не найдено')
    return
  }

  window.BX.calendar({
    node: target,
    field: input,
    bTime: false,
    bHideTime: true,
    callback: (date: string | Date) => {
      const formattedDate = formatDate(date)

      input.value = formattedDate

      if (input.name === 'form_date_77') {
        birthDate.value = formattedDate
        validateDate()
      }
    }
  })
}

function closeSuccessNotification() {
  status.value = Status.pristine
  lock.value = false
  birthDate.value = ''
  hasError.value = false
  isTouched.value = false
  resetForm()

  if (props.modal) {
    emit('close')
  }
}

function closeErrorNotification() {
  status.value = Status.dirty
  lock.value = false
}

onMounted(() => {
  setFieldValue('form_date_77', birthDate.value)
})

const onSubmit = handleSubmit(async () => {
  validateDate()

  if (hasError.value) {
    return
  }

  if (!form.value) {
    console.warn('Form element is not available.')
    return
  }

  if (!window.BX) {
    console.warn('Требуется window.BX')
    return
  }

  lock.value = true

  const formData = new FormData(form.value)

  try {
    await window.BX.ajax.runAction('platform:main.api.form.submit', {
      data: formData
    })
    status.value = Status.success
  } catch (e) {
    console.log('form error', e)
    status.value = Status.error
  } finally {
    lock.value = false
  }
})
</script>

<template>
  <div
    class="component-appointment-form"
    :class="{ 'form-in-modal': modal }"
  >
    <div class="form-container form-container--appointment content">
      <div
        v-if="isNotSuccess"
        class="form-header mb-4"
      >
      </div>

      <div v-if="isSuccess" class="notification is-link is-light">
        <p class="text-xl">Спасибо! Ваше сообщение отправлено.</p>
        <p style="color: red;">После заполнения формы на указанную вами электронную почту в течение 1 рабочего дня будет выслан пароль для входа в личный кабинет.</p>
        <p style="color: red;">Далее необходимо зайти на портал ОНЛАЙН-записи <strong>konsultanttula.infoclinica.ru</strong></p>
      </div>
      <FormMessages
        :status="status"
        :show-home-link="false"
        @close:error="closeErrorNotification"
        @close:success="closeSuccessNotification"
        successTitle=""
        successText=""
        :class="isSuccess ? 'p-success' : ''"
      />


      <div
        v-if="isNotSuccess"
        class="form-wrapper"
      >
        <form
          ref="form"
          novalidate
          @submit.prevent="onSubmit"
        >
          <FieldHidden name="WEB_FORM_ID" />
          <input type="hidden" class="input" name="form_hidden_80" value="Да">

          <div class="columns is-multiline">
            <div class="column is-6-tablet">
              <p class="is-size-5 mb-0"><strong>ВЗРОСЛЫЙ</strong></p>
              <hr class="mt-2">

              <FieldText
                name="form_text_85"
                label="Фамилия"
                help="Обязательне поле."
                :disabled="lock"
                :asterisk="false"

              />
              <FieldText
                name="form_text_86"
                label="Имя"
                help="Обязательне поле."
                :disabled="lock"
                :asterisk="false"
              />
              <FieldText
                name="form_text_87"
                label="Отчество"
                help="Обязательне поле."
                :disabled="lock"
                :asterisk="false"
              />

              <div class="field field-date">
                <div class="control">
                  <input
                    v-model="birthDate"
                    type="text"
                    name="form_date_77"
                    placeholder="Дата рождения* (DD.MM.YYYY)"
                    class="input"
                    :class="{ 'is-invalid': hasError }"
                    aria-required="true"
                    :aria-invalid="hasError"
                    autocomplete="off"
                    :disabled="lock"
                    @input="validateDate"
                  >
                    <span
                      class="calendar-icon i-lucide-calendar-days is-size-2 has-text-primary ml-2"
                      @click="openCalendar"
                    />
                </div>
                <p class="help" v-if="!hasError">Обязательне поле.</p>

                <p v-if="hasError" class="help">
                  <span class="error">Обязательное поле</span>
                </p>
              </div>

              <FieldText
                name="form_text_94"
                label="СНИЛС"
                help="Обязательне поле."
                :disabled="lock"
                :asterisk="false"
              />

              <FieldEmail
                name="form_email_79"
                :disabled="lock"
                :asterisk="false"
              />

              <FieldPhone
                name="form_text_78"
                :disabled="lock"
                :asterisk="false"
              />
            </div>

            <div class="column is-6-tablet">
              <p class="is-size-5 mb-0"><strong>ДЕТИ (если хотите записать ребенка)</strong></p>
              <hr class="mt-2">
              <div>
                <p class="is-size-5 mb-2"><strong>1-й ребёнок</strong></p>

                <FieldText
                  name="form_text_88"
                  label="Фамилия"
                  :disabled="false"
                  :asterisk="false"
                  :hideHelp="true"
                />
                <FieldText
                  name="form_text_90"
                  label="Имя"
                  :disabled="lock"
                  :asterisk="false"
                  :hideHelp="true"
                />
                <FieldText
                  name="form_text_92"
                  label="Отчество"
                  :disabled="lock"
                  :asterisk="false"
                  :hideHelp="true"
                />

                <div class="field field-date">
                  <div class="control">
                    <input
                      type="text"
                      name="form_date_83"
                      placeholder="Дата рождения* (DD.MM.YYYY)"
                      class="input"
                      autocomplete="off"
                    >
                    <span
                      class="calendar-icon i-lucide-calendar-days is-size-2 has-text-primary ml-2"
                      @click="openCalendar"
                    />
                  </div>
                </div>

                <FieldText
                  name="form_text_95"
                  label="СНИЛС"
                  :disabled="lock"
                  :asterisk="false"
                />
              </div>

              <div class="pt-6">
                <p class="is-size-5 mb-2"><strong>2-й ребёнок</strong></p>

                <FieldText
                  name="form_text_89"
                  label="Фамилия"
                  :disabled="lock"
                  :asterisk="false"
                  :hideHelp="true"
                />
                <FieldText
                  name="form_text_91"
                  label="Имя"
                  :disabled="lock"
                  :asterisk="false"
                  :hideHelp="true"
                />
                <FieldText
                  name="form_text_93"
                  label="Отчество"
                  :disabled="lock"
                  :asterisk="false"
                  :hideHelp="true"
                />

                <div class="field field-date">
                  <div class="control">
                    <input
                      type="text"
                      name="form_date_84"
                      placeholder="Дата рождения* (DD.MM.YYYY)"
                      class="input"
                      autocomplete="off"
                    >
                    <span
                      class="calendar-icon i-lucide-calendar-days is-size-2 has-text-primary ml-2"
                      @click="openCalendar"
                    />
                  </div>
                </div>

                <FieldText
                  name="form_text_96"
                  label="СНИЛС"
                  :disabled="lock"
                  :asterisk="false"
                />
              </div>
            </div>

          </div>

          <div class="form-privacy">
            <FormLegalNotice
              privacy-link="/assets/files/legal-information/Policy-personal.pdf"
              agreement-link="/agreement/personal-information/"
              terms-link="/agreement/"
              hide-personal
            />

            <div class="switch-pr">
              <o-switch
                v-model="value"
                name="agreement"
                label="Согласен с условиями *"
              />
            </div>
          </div>

          <div class="buttons pt-5">
            <button
              class="button is-danger has-text-white radius-5"
              type="submit"
              :disabled="lock || !value"
            >
              Отправить
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<style>
.form-container--appointment .form-privacy a {
  color: #2e82ff;
}

.form-container--appointment .o-switch__input:checked {
  background-color: #fe3294;
}

.form-container--appointment .select select,
.form-container--appointment .input,
.form-container--appointment .textarea {
  color: #000;
}

.error {
  color: red;
}

.is-invalid {
  border-color: red;
}

.p-success .notification {
  padding: 0;
}
</style>
